import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  checked: boolean;
}

export interface DriverLoad {
  id: string;
  truckId: string;
  driverName: string;
  loadType: string;
  customerName: string;
  bolNumber: string;
  pickupLocation: string;
  deliveryLsd: string;
  deliveryDescription?: string;
  departureTime: string;
  duration: string;
  cargo: { item: string; weight: string }[];
  totalWeight: string;
  specialInstructions?: string;
  /** The load_status enum as sent, except `booked`, which reads 'pending' here. */
  status: string;
  /** `loads.drive_time_minutes`; null when not recorded. Never a made-up figure. */
  driveTimeMinutes: number | null;
  departedAt?: string;
  estArrival?: string;
  acceptedAt?: string;
  progressPercent?: number;
  timeRemaining?: string;
  todayLoads?: { label: string; status: string; time?: string }[];
  moveJobId?: string;
  moveJobName?: string;
  moveSequenceNumber?: number;
  /**
   * The rig move's master BOL counts (GET /trucking/move-jobs/:id → masterBol),
   * each null when not sent; `null` itself when the move job has no master BOL
   * yet. Never 0 for an absent count.
   */
  moveJobProgress?: { total: number | null; delivered: number | null; signed: number | null; status: string | null } | null;
}

// Dynamic checklist generation based on load type and conditions
export function getChecklistForLoad(load: DriverLoad): ChecklistItem[] {
  const items: ChecklistItem[] = [
    {
      id: "bol-review",
      label: "Review BOL and cargo manifest",
      description: `${load.cargo.length} items · ${load.totalWeight}`,
      checked: false,
    },
  ];

  const instr = (load.specialInstructions || "").toLowerCase();
  if (instr.includes("h2s")) {
    items.push({
      id: "h2s-monitor",
      label: "H2S monitor calibrated",
      description: "Required — H2S area",
      checked: false,
    });
  }
  if (instr.includes("hazmat") || instr.includes("dangerous")) {
    items.push({
      id: "dg-placards",
      label: "DG placards displayed",
      description: "Required for dangerous goods transport",
      checked: false,
    });
  }

  const lt = load.loadType.toLowerCase();
  if (["float", "lowboy", "hiboy", "winch tractor", "bed truck"].includes(lt)) {
    items.push({
      id: "load-securement",
      label: "Load securement verified",
      description: "Chains, binders, straps inspected",
      checked: false,
    });
  }
  if (["vacuum", "water", "combo", "acid", "frac"].includes(lt)) {
    items.push({
      id: "tank-valves",
      label: "Tank valves and seals checked",
      description: "No leaks, caps secured",
      checked: false,
    });
  }
  if (lt === "hot oiler" || lt === "steamer") {
    items.push({
      id: "temp-check",
      label: "Temperature and pressure verified",
      description: "Within safe operating range",
      checked: false,
    });
  }

  items.push(
    {
      id: "pre-trip",
      label: "Vehicle pre-trip inspection",
      description: "Tires, lights, straps, load security",
      checked: false,
    },
    {
      id: "driver-bol-sign",
      label: "Driver signature on BOL",
      description: "Confirms cargo received at pickup",
      checked: false,
    }
  );

  return items;
}

// ── Real API hooks ──────────────────────────────────

export const useDriverLoad = (loadId: string) => useQuery({
  queryKey: ['trucking-load', loadId],
  queryFn: () => apiClient.get(`/trucking/loads/${loadId}`).then(r => r.data?.data ?? r.data),
  enabled: !!loadId,
});

/*
  `useAcceptLoad` and `useDeclineLoad` are removed.

  They called `PATCH /trucking/loads/:id/accept` and `/decline`. Both routes
  exist on the API and both are dead: they write `status = 'accepted'` /
  `'declined'` into a `load_status` enum whose labels are
  `booked, dispatched, en_route, arrived, delivered, cancelled, signed`, and
  stamp `accepted_at` / `declined_at`, which are not columns on `loads`. Read
  from production 2026-09-04. Every call 500s — a driver accepting a load has
  never worked, on any tenant, since the routes were written.

  Deleted rather than left with a comment, because a hook sitting here is an
  invitation: the next screen that needs an Accept button finds it, uses it,
  and inherits the 500. When the API can hold the state, add them back.
*/

export const useDepartLoad = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, odometer_at_departure }: { loadId: string; odometer_at_departure: number }) =>
      apiClient.patch(`/trucking/loads/${loadId}/depart`, { odometer_at_departure }).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-loads'] });
      qc.invalidateQueries({ queryKey: ['trucking-load', vars.loadId] });
    },
  });
};

export const useArriveLoad = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, arrived_at }: { loadId: string; arrived_at: string }) =>
      apiClient.patch(`/trucking/loads/${loadId}/arrive`, { arrived_at }).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-loads'] });
      qc.invalidateQueries({ queryKey: ['trucking-load', vars.loadId] });
    },
  });
};

export const useDeliverLoad = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, payload }: { loadId: string; payload: any }) =>
      apiClient.patch(`/trucking/loads/${loadId}/deliver`, payload).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-loads'] });
      qc.invalidateQueries({ queryKey: ['trucking-load', vars.loadId] });
    },
  });
};

/*
 * Transform GET /trucking/loads/:id to the DriverLoad shape.
 *
 * THE ROUTE IS FLAT, AND THIS READ NESTED OBJECTS IT NEVER SENDS
 * (fix/driver-app-trucking). It sends `l.*` plus `truck_unit_number`,
 * `customer_name`, `driver_first_name` / `driver_last_name` and, since
 * api#1109, `trailer_unit_number`. This read `load.truck.unit_number`,
 * `load.customer.name` and `load.truck.driver_name`, so on Apex Hauling the
 * driver saw a blank customer, the truck as its raw UUID (the `truck_id`
 * fallback) and "Driver" for their own name. The nested spellings stay as a
 * second read for an older payload; a UUID is never shown as a unit.
 */
export function apiLoadToDriverLoad(data: any): DriverLoad {
  const load = data?.load || data;
  const customerName = load.customer_name || load.customer?.name || '';
  const driverName = [load.driver_first_name, load.driver_last_name].filter(Boolean).join(' ') || load.truck?.driver_name || '';
  return {
    id: load.id || '',
    truckId: load.truck_unit_number || load.truck?.unit_number || '',
    driverName,
    loadType: (load.load_type || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    customerName,
    bolNumber: load.bol_number || '',
    pickupLocation: load.pickup_location || '',
    deliveryLsd: load.delivery_location || '',
    deliveryDescription: customerName ? `${customerName} · ${load.delivery_location}` : load.delivery_location,
    // Minutes, as the route stores them; null until the load has been driven.
    driveTimeMinutes: typeof load.drive_time_minutes === 'number' && Number.isFinite(load.drive_time_minutes) ? load.drive_time_minutes : null,
    // `scheduled_departure` is a Postgres time, sent as "08:00:00"; a driver reads "08:00".
    departureTime: (typeof load.scheduled_departure === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(load.scheduled_departure) ? load.scheduled_departure.slice(0, 5) : load.scheduled_departure) || load.departed_at || '',
    duration: load.est_duration_hours ? `~${load.est_duration_hours} hours` : '',
    cargo: (data?.cargoItems || []).map((c: any) => ({ item: c.description, weight: c.weight_kg ? `${c.weight_kg} ${c.unit || 'kg'}` : '' })),
    totalWeight: '',
    specialInstructions: load.special_instructions || undefined,
    status: load.status === 'booked' ? 'pending' : load.status,
    departedAt: load.departed_at || undefined,
    estArrival: load.arrived_at || undefined,
    progressPercent: undefined,
    timeRemaining: undefined,
    todayLoads: [],
    moveJobId: load.move_job_id || undefined,
    moveJobName: load.move_job_name || undefined,
    moveSequenceNumber: load.sequence_number ?? undefined,
  };
}
